const normalizeText = (value: unknown): string =>
  String(value ?? '').replace(/\s+/g, ' ').trim();

const asRecord = (value: unknown): Record<string, unknown> | null => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
);

const fieldLabels: Record<string, string> = {
  amount: 'Refund amount',
  originalPaymentEntryId: 'Original payment',
  reasonNotes: 'Reason notes',
};

const readNestedMessage = (error: unknown): string => {
  if (typeof error === 'string') {
    return normalizeText(error);
  }
  if (error instanceof Error) {
    return normalizeText(error.message);
  }

  const record = asRecord(error);
  if (!record) return '';

  const direct = normalizeText(record.message);
  if (direct) return direct;

  const data = asRecord(record.data);
  const nested = normalizeText(data?.message);
  if (nested) return nested;

  const errors = Array.isArray(record.errors) ? record.errors : [];
  const firstErrorMessage = normalizeText(asRecord(errors[0])?.message);
  if (firstErrorMessage) return firstErrorMessage;

  return '';
};

const parseIssueList = (message: string): Array<Record<string, unknown>> => {
  const start = message.indexOf('[');
  if (start < 0) return [];

  try {
    const parsed = JSON.parse(message.slice(start));
    return Array.isArray(parsed)
      ? parsed.filter((entry): entry is Record<string, unknown> => Boolean(asRecord(entry)))
      : [];
  } catch {
    return [];
  }
};

const formatValidationIssue = (issue: Record<string, unknown>): string => {
  const path = Array.isArray(issue.path)
    ? issue.path.map((entry) => String(entry)).filter(Boolean)
    : [];
  const field = path[path.length - 1] ?? '';
  const label = fieldLabels[field] ?? 'Refund field';
  const message = normalizeText(issue.message);

  if (field === 'originalPaymentEntryId') {
    return 'Select an original Stripe payment before issuing this refund.';
  }
  if (field === 'reasonNotes') {
    return 'Reason notes are required when Reason is Other.';
  }
  if (message) {
    return `${label}: ${message}`;
  }
  return `${label} needs attention before this refund can be issued.`;
};

export const extractTuitionActionErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  const message = readNestedMessage(error);
  if (!message) return fallback;

  const issues = parseIssueList(message);
  if (issues.length > 0) {
    return formatValidationIssue(issues[0]);
  }

  if (/originalPaymentEntryId/i.test(message)) {
    return 'Select an original Stripe payment before issuing this refund.';
  }

  return message || fallback;
};
